import { c as _c } from "react/compiler-runtime";
import { buildConfluentAllStarCaseName } from "@/configuration/groups/confluent-all-star/case-name";
import { DecisionMetadata, ReferralWorkflowTimeline, type ReferralWorkflowStatus, type ReferralWorkflowStep } from "@/configuration/groups/confluent-all-star/components/referral-timeline";
import { CONFLUENT_ALL_STAR_TIMEZONE } from "@/configuration/groups/confluent-all-star/configuration";
import { getConfluentReferralStepKeys, type ConfluentReferralStepKey } from "@/configuration/groups/confluent-all-star/workflows/step-order";
import { FieldLayout, type Field } from "@/src/components/cards/common";
import { DiagnosisListTable } from "@/src/components/cards/diagnosis-list-table";
import PatientCard from "@/src/components/cards/patient-card/patient-card";
import { CopyableContent } from "@/src/components/copyable-content";
import { ReferralExportResult } from "@/src/components/tasks/referral-export-result";
import { WorkflowStepInformationNeeded } from "@/src/components/tasks/workflow-step-information-needed";
import { WorkflowStepResumeButton } from "@/src/components/tasks/workflow-step-resume-button";
import { formatDateMmDdYyyy, getCurrentDateTimeInTimezone } from "@/src/lib/utils/date-and-phone-utils";
import { StepKey } from "@/src/lib/workflows/steps/types";
import { type Diagnosis, type Referral } from "@/src/models/documents/referral";
import { FileType } from "@/src/models/file";
import { TaskStatus, type Task } from "@/src/models/task";
import type { TaskApproval } from "@/src/models/task-approval";
import { TaskStepStatus, type TaskStep } from "@/src/models/task-step";
function stepStatus(step: TaskStep | undefined, confirmed: boolean, needsAction = false, skipped = false): ReferralWorkflowStatus {
  if (!step) return "not_run";
  if (step.status === TaskStepStatus.FAILED) return "failed";
  if (needsAction || step.status === TaskStepStatus.PAUSED) return "needs_action";
  if (step.status === TaskStepStatus.ASYNC) return "waiting";
  // Early exits are also persisted as completed. Require success evidence before showing the checkmark.
  if (step.status === TaskStepStatus.COMPLETED && confirmed) return skipped ? "not_needed" : "automated";
  return "needs_action";
}
function outputId(step: TaskStep | undefined, key: string): string | null {
  const value = step?.output?.[key];
  return typeof value === "string" && value.trim() ? value : null;
}
function recordOrigin(created: unknown): string {
  return created === true ? "New" : created === false ? "Existing" : "Not recorded";
}
function formatReferringProvider(provider: Referral["referringProvider"]): string | null {
  if (!provider) return null;
  const name = [provider.givenName, provider.familyName].filter(Boolean).join(" ").trim();
  const npi = provider.npiNumber ? `NPI ${provider.npiNumber}` : null;
  return [name || null, npi].filter(Boolean).join(" · ") || null;
}
export function ConfluentReferralSteps(t0) {
  const $ = _c(175);
  const {
    task,
    taskSteps,
    referral,
    pendingApproval
  } = t0;
  let hasAsyncStep;
  let hasInformationNeededStep;
  let isCompleted;
  let isProcessing;
  let needsAttention;
  let note;
  let steps;
  if ($[0] !== pendingApproval || $[1] !== referral || $[2] !== task || $[3] !== taskSteps) {
    const orderedTaskSteps = taskSteps.toSorted(_temp);
    const findStep = key => orderedTaskSteps.find(step => step.stepKey === key);
    const patientStep = findStep(StepKey.ENSURE_PATIENT);
    const faxStep = findStep(StepKey.REQUIRE_PATIENT_PHONE);
    const faxSkipped = faxStep?.output?.outcome === "skipped";
    const faxId = outputId(faxStep, "faxId");
    const caseStep = findStep(StepKey.CLINICIENT_ENSURE_CASE);
    const configureStep = findStep(StepKey.CLINICIENT_CONFIGURE_CASE);
    const uploadStep = findStep(StepKey.CLINICIENT_CASE_DOCUMENT_UPLOAD);
    const noteStep = findStep(StepKey.CLINICIENT_DOCUMENT_NOTE);
    const insuranceStep = findStep(StepKey.CLINICIENT_SYNC_INSURANCE);
    const authorizationStep = findStep(StepKey.CLINICIENT_ENSURE_AUTHORIZATION);
    const authorizationUploadStep = findStep(StepKey.CLINICIENT_AUTHORIZATION_DOCUMENT_UPLOAD);
    const authorizationTaskStep = findStep(StepKey.CLINICIENT_AUTHORIZATION_TASK);
    const authorizationId = outputId(authorizationStep, "authorizationEhrId");
    const authorizationDocumentId = outputId(authorizationUploadStep, "documentEhrId");
    const authorizationTaskId = outputId(authorizationTaskStep, "taskEhrId");
    const authorizationSkipped = authorizationStep?.output?.outcome === "skipped";
    const authorizationUploadSkipped = authorizationUploadStep?.output?.outcome === "skipped";
    const authorizationTaskSkipped = authorizationTaskStep?.output?.outcome === "skipped";
    const exportStep = findStep(StepKey.REFERRAL_SHEET_EXPORT);
    const exportRange = outputId(exportStep, "updatedRange");
    const exportSkippedForExistingAppointment = exportRange === "skipped:existing-appointment";
    const patientId = outputId(patientStep, "patientEhrId");
    const caseId = outputId(caseStep, "patientCaseEhrId");
    const documentId = outputId(uploadStep, "documentEhrId");
    const noteId = outputId(noteStep, "noteEhrId");
    const insurancePlanId = outputId(insuranceStep, "insurancePlanId");
    const existingCase = caseStep?.output?.created === false;
    const insuranceSkipped = insuranceStep?.output?.outcome === "skipped";
    const lastStep = orderedTaskSteps.at(-1);
    const onHold = task.status === TaskStatus.AWAITING_INFORMATION || task.status === TaskStatus.REVIEW_NEEDED;
    const configureHeld = onHold && configureStep !== undefined && configureStep.id === lastStep?.id;
    let authorizationEnd;
    let authorizationNumber;
    let authorizationStart;
    let bodyPart;
    let dateOfOnset;
    let diagnoses;
    let isContinuationOfCare;
    let isWorkersComp;
    let numberOfVisits;
    let patient;
    let primaryEhrPayerName;
    let reasonForReferral;
    let secondaryEhrPayerName;
    let serviceLine;
    let t1;
    if ($[11] !== referral.authorizationNumber || $[12] !== referral.bodyPart || $[13] !== referral.dateOfOnset || $[14] !== referral.diagnosisList || $[15] !== referral.isContinuationOfCare || $[16] !== referral.isPostOpReferral || $[17] !== referral.isWorkersComp || $[18] !== referral.numberOfVisits || $[19] !== referral.patient || $[20] !== referral.primaryEhrPayerName || $[21] !== referral.reasonForReferral || $[22] !== referral.secondaryEhrPayerName || $[23] !== referral.section?.reviewedContent || $[24] !== referral.serviceLine || $[25] !== task.priorAuthorization) {
      const reviewedContent = (referral.section?.reviewedContent ?? {}) as Record<string, unknown>;
      const reviewedString = key_0 => {
        const value = reviewedContent[key_0];
        return typeof value === "string" && value.trim() ? value : undefined;
      };
      const reviewedBoolean = key_1 => typeof reviewedContent[key_1] === "boolean" ? reviewedContent[key_1] as boolean : undefined;
      const reviewedNumber = key_2 => typeof reviewedContent[key_2] === "number" ? reviewedContent[key_2] as number : undefined;
      const priorAuthorization = task.priorAuthorization;
      bodyPart = reviewedString("bodyPart") ?? referral.bodyPart ?? null;
      serviceLine = reviewedString("serviceLine") ?? referral.serviceLine ?? null;
      let t2;
      if ($[41] !== referral.dateOfOnset || $[42] !== reviewedString) {
        t2 = reviewedString("dateOfOnset") ?? referral.dateOfOnset ?? null;
        $[41] = referral.dateOfOnset;
        $[42] = reviewedString;
        $[43] = t2;
      } else {
        t2 = $[43];
      }
      dateOfOnset = t2;
      reasonForReferral = reviewedString("reasonForReferral") ?? referral.reasonForReferral ?? null;
      primaryEhrPayerName = reviewedString("primaryEhrPayerName") ?? reviewedString("ehrPayerName") ?? referral.primaryEhrPayerName ?? null;
      let t3;
      if ($[44] !== referral.secondaryEhrPayerName || $[45] !== reviewedString) {
        t3 = reviewedString("secondaryEhrPayerName") ?? referral.secondaryEhrPayerName ?? null;
        $[44] = referral.secondaryEhrPayerName;
        $[45] = reviewedString;
        $[46] = t3;
      } else {
        t3 = $[46];
      }
      secondaryEhrPayerName = t3;
      let t4;
      if ($[47] !== referral.isWorkersComp || $[48] !== reviewedBoolean) {
        t4 = reviewedBoolean("isWorkersComp") ?? referral.isWorkersComp ?? false;
        $[47] = referral.isWorkersComp;
        $[48] = reviewedBoolean;
        $[49] = t4;
      } else {
        t4 = $[49];
      }
      isWorkersComp = t4;
      const isPostOpReferral = reviewedBoolean("isPostOpReferral") ?? referral.isPostOpReferral ?? null;
      const isPostOpPriorAuth = reviewedBoolean("isPostOpPriorAuth") ?? priorAuthorization?.isPostOpPriorAuth ?? null;
      isContinuationOfCare = (reviewedBoolean("isContinuationOfCare") ?? referral.isContinuationOfCare) === true;
      let t5;
      if ($[50] !== referral.numberOfVisits || $[51] !== reviewedNumber) {
        t5 = reviewedNumber("numberOfVisits") ?? referral.numberOfVisits ?? null;
        $[50] = referral.numberOfVisits;
        $[51] = reviewedNumber;
        $[52] = t5;
      } else {
        t5 = $[52];
      }
      numberOfVisits = t5;
      let t6;
      if ($[53] !== priorAuthorization?.authorizationNumber || $[54] !== referral.authorizationNumber || $[55] !== reviewedString) {
        t6 = reviewedString("authorizationNumber") ?? priorAuthorization?.authorizationNumber ?? referral.authorizationNumber ?? null;
        $[53] = priorAuthorization?.authorizationNumber;
        $[54] = referral.authorizationNumber;
        $[55] = reviewedString;
        $[56] = t6;
      } else {
        t6 = $[56];
      }
      authorizationNumber = t6;
      let t7;
      if ($[57] !== priorAuthorization?.startDate || $[58] !== reviewedString) {
        t7 = reviewedString("startDate") ?? priorAuthorization?.startDate ?? null;
        $[57] = priorAuthorization?.startDate;
        $[58] = reviewedString;
        $[59] = t7;
      } else {
        t7 = $[59];
      }
      authorizationStart = t7;
      let t8;
      if ($[60] !== priorAuthorization?.endDate || $[61] !== reviewedString) {
        t8 = reviewedString("endDate") ?? priorAuthorization?.endDate ?? null;
        $[60] = priorAuthorization?.endDate;
        $[61] = reviewedString;
        $[62] = t8;
      } else {
        t8 = $[62];
      }
      authorizationEnd = t8;
      const reviewedDiagnoses = Array.isArray(reviewedContent.diagnosisList) ? reviewedContent.diagnosisList as Diagnosis[] : null;
      diagnoses = reviewedDiagnoses ?? referral.diagnosisList ?? [];
      patient = referral.patient;
      t1 = buildConfluentAllStarCaseName({
        diagnosisList: diagnoses.length ? diagnoses : null,
        reasonForReferral,
        bodyPart,
        primaryEhrPayerName,
        serviceLine,
        dateOfBirth: patient.dateOfBirth,
        isPostOpReferral,
        isPostOpPriorAuth
      });
      $[11] = referral.authorizationNumber;
      $[12] = referral.bodyPart;
      $[13] = referral.dateOfOnset;
      $[14] = referral.diagnosisList;
      $[15] = referral.isContinuationOfCare;
      $[16] = referral.isPostOpReferral;
      $[17] = referral.isWorkersComp;
      $[18] = referral.numberOfVisits;
      $[19] = referral.patient;
      $[20] = referral.primaryEhrPayerName;
      $[21] = referral.reasonForReferral;
      $[22] = referral.secondaryEhrPayerName;
      $[23] = referral.section?.reviewedContent;
      $[24] = referral.serviceLine;
      $[25] = task.priorAuthorization;
      $[26] = authorizationEnd;
      $[27] = authorizationNumber;
      $[28] = authorizationStart;
      $[29] = bodyPart;
      $[30] = dateOfOnset;
      $[31] = diagnoses;
      $[32] = isContinuationOfCare;
      $[33] = isWorkersComp;
      $[34] = numberOfVisits;
      $[35] = patient;
      $[36] = primaryEhrPayerName;
      $[37] = reasonForReferral;
      $[38] = secondaryEhrPayerName;
      $[39] = serviceLine;
      $[40] = t1;
    } else {
      authorizationEnd = $[26];
      authorizationNumber = $[27];
      authorizationStart = $[28];
      bodyPart = $[29];
      dateOfOnset = $[30];
      diagnoses = $[31];
      isContinuationOfCare = $[32];
      isWorkersComp = $[33];
      numberOfVisits = $[34];
      patient = $[35];
      primaryEhrPayerName = $[36];
      reasonForReferral = $[37];
      secondaryEhrPayerName = $[38];
      serviceLine = $[39];
      t1 = $[40];
    }
    const generatedCaseName = t1;
    const syncedCaseName = outputId(caseStep, "caseName") ?? generatedCaseName;
    let t2;
    if ($[63] !== referral.dateOfReferral) {
      t2 = formatDateMmDdYyyy(referral.dateOfReferral);
      $[63] = referral.dateOfReferral;
      $[64] = t2;
    } else {
      t2 = $[64];
    }
    const referralDate = t2;
    const documentDescription = referralDate ? `${referralDate} Referral` : "Referral";
    const authorizationTaskAction = isContinuationOfCare ? "Re-Authorization" : "Authorization";
    const authorizationTaskDueDate = getCurrentDateTimeInTimezone(CONFLUENT_ALL_STAR_TIMEZONE).plus({
      days: 1
    }).toFormat("MM/dd/yyyy");
    let t3;
    if ($[65] !== bodyPart) {
      t3 = () => bodyPart;
      $[65] = bodyPart;
      $[66] = t3;
    } else {
      t3 = $[66];
    }
    let t4;
    if ($[67] !== dateOfOnset) {
      t4 = () => dateOfOnset;
      $[67] = dateOfOnset;
      $[68] = t4;
    } else {
      t4 = $[68];
    }
    let t5;
    if ($[69] !== reasonForReferral) {
      t5 = () => reasonForReferral;
      $[69] = reasonForReferral;
      $[70] = t5;
    } else {
      t5 = $[70];
    }
    let t6;
    if ($[71] !== referral.referringProvider) {
      t6 = () => formatReferringProvider(referral.referringProvider);
      $[71] = referral.referringProvider;
      $[72] = t6;
    } else {
      t6 = $[72];
    }
    const caseIntakeFields = [{
      key: "caseName",
      label: "Case Name",
      getValue: () => syncedCaseName
    }, {
      key: "bodyPart",
      label: "Body Part",
      getValue: t3
    }, {
      key: "dateOfOnset",
      label: "Date of Onset",
      type: "date",
      getValue: t4
    }, {
      key: "reasonForReferral",
      label: "Reason for Referral",
      colSpan: 2,
      wrap: true,
      getValue: t5
    }, {
      key: "referringProvider",
      label: "Referring Provider",
      getValue: t6
    }];
    let t7;
    if ($[73] !== dateOfOnset) {
      t7 = {
        key: "dateOfOnset",
        label: "Date of Onset",
        type: "date",
        getValue: () => dateOfOnset
      };
      $[73] = dateOfOnset;
      $[74] = t7;
    } else {
      t7 = $[74];
    }
    let t8;
    if ($[75] !== serviceLine) {
      t8 = {
        key: "serviceLine",
        label: "Service Line",
        getValue: () => serviceLine
      };
      $[75] = serviceLine;
      $[76] = t8;
    } else {
      t8 = $[76];
    }
    let t9;
    if ($[77] !== referral.receivingLocation?.name) {
      t9 = {
        key: "facility",
        label: "Facility",
        getValue: () => referral.receivingLocation?.name ?? null
      };
      $[77] = referral.receivingLocation?.name;
      $[78] = t9;
    } else {
      t9 = $[78];
    }
    let t10;
    if ($[79] !== isWorkersComp) {
      t10 = {
        key: "isWorkersComp",
        label: "Workers' Comp",
        getValue: () => isWorkersComp ? "Yes" : "No"
      };
      $[79] = isWorkersComp;
      $[80] = t10;
    } else {
      t10 = $[80];
    }
    let t11;
    if ($[81] !== referral.referringProvider) {
      t11 = {
        key: "referringProvider",
        label: "Referring Provider",
        getValue: () => formatReferringProvider(referral.referringProvider)
      };
      $[81] = referral.referringProvider;
      $[82] = t11;
    } else {
      t11 = $[82];
    }
    let t12;
    if ($[83] !== t10 || $[84] !== t11 || $[85] !== t7 || $[86] !== t8 || $[87] !== t9) {
      t12 = [t7, t8, t9, t10, t11];
      $[83] = t10;
      $[84] = t11;
      $[85] = t7;
      $[86] = t8;
      $[87] = t9;
      $[88] = t12;
    } else {
      t12 = $[88];
    }
    const caseDetailFields = t12;
    let t13;
    if ($[89] !== referral.referringLocation?.name) {
      t13 = {
        key: "referringLocation",
        label: "Referring Provider Location",
        colSpan: 3,
        getValue: () => referral.referringLocation?.name ?? null
      };
      $[89] = referral.referringLocation?.name;
      $[90] = t13;
    } else {
      t13 = $[90];
    }
    let t14;
    if ($[91] !== primaryEhrPayerName) {
      t14 = {
        key: "primaryEhrPayerName",
        label: "Clinicient Insurance Name",
        getValue: () => primaryEhrPayerName
      };
      $[91] = primaryEhrPayerName;
      $[92] = t14;
    } else {
      t14 = $[92];
    }
    let t15;
    if ($[93] !== referral.primaryCoverage?.subscriberId) {
      t15 = {
        key: "primarySubscriber",
        label: "Subscriber Number",
        getValue: () => referral.primaryCoverage?.subscriberId ?? null
      };
      $[93] = referral.primaryCoverage?.subscriberId;
      $[94] = t15;
    } else {
      t15 = $[94];
    }
    let t16;
    if ($[95] !== referral.primaryCoverage?.groupNumber) {
      t16 = {
        key: "primaryGroup",
        label: "Group Number",
        getValue: () => referral.primaryCoverage?.groupNumber ?? null
      };
      $[95] = referral.primaryCoverage?.groupNumber;
      $[96] = t16;
    } else {
      t16 = $[96];
    }
    let t17;
    if ($[97] !== t13 || $[98] !== t14 || $[99] !== t15 || $[100] !== t16) {
      t17 = [t13, t14, t15, t16];
      $[97] = t13;
      $[98] = t14;
      $[99] = t15;
      $[100] = t16;
      $[101] = t17;
    } else {
      t17 = $[101];
    }
    const primaryInsuranceFields = t17;
    let t18;
    if ($[102] !== secondaryEhrPayerName) {
      t18 = {
        key: "secondaryEhrPayerName",
        label: "Clinicient Insurance Name",
        getValue: () => secondaryEhrPayerName
      };
      $[102] = secondaryEhrPayerName;
      $[103] = t18;
    } else {
      t18 = $[103];
    }
    let t19;
    if ($[104] !== referral.secondaryCoverage?.subscriberId) {
      t19 = {
        key: "secondarySubscriber",
        label: "Subscriber Number",
        getValue: () => referral.secondaryCoverage?.subscriberId ?? null
      };
      $[104] = referral.secondaryCoverage?.subscriberId;
      $[105] = t19;
    } else {
      t19 = $[105];
    }
    let t20;
    if ($[106] !== referral.secondaryCoverage?.groupNumber) {
      t20 = {
        key: "secondaryGroup",
        label: "Group Number",
        getValue: () => referral.secondaryCoverage?.groupNumber ?? null
      };
      $[106] = referral.secondaryCoverage?.groupNumber;
      $[107] = t20;
    } else {
      t20 = $[107];
    }
    let t21;
    if ($[108] !== t18 || $[109] !== t19 || $[110] !== t20) {
      t21 = [t18, t19, t20];
      $[108] = t18;
      $[109] = t19;
      $[110] = t20;
      $[111] = t21;
    } else {
      t21 = $[111];
    }
    const secondaryInsuranceFields = t21;
    let t22;
    if ($[112] !== authorizationNumber) {
      t22 = {
        key: "authorizationNumber",
        label: "Authorization Number",
        getValue: () => authorizationNumber
      };
      $[112] = authorizationNumber;
      $[113] = t22;
    } else {
      t22 = $[113];
    }
    let t23;
    if ($[114] !== authorizationStart) {
      t23 = {
        key: "authorizationStart",
        label: "Start Date",
        type: "date",
        getValue: () => authorizationStart
      };
      $[114] = authorizationStart;
      $[115] = t23;
    } else {
      t23 = $[115];
    }
    let t24;
    if ($[116] !== authorizationEnd) {
      t24 = {
        key: "authorizationEnd",
        label: "End Date",
        type: "date",
        getValue: () => authorizationEnd
      };
      $[116] = authorizationEnd;
      $[117] = t24;
    } else {
      t24 = $[117];
    }
    let t25;
    if ($[118] !== numberOfVisits) {
      t25 = {
        key: "numberOfVisits",
        label: "Number of Visits",
        getValue: () => numberOfVisits != null ? String(numberOfVisits) : null
      };
      $[118] = numberOfVisits;
      $[119] = t25;
    } else {
      t25 = $[119];
    }
    let t26;
    if ($[120] !== t22 || $[121] !== t23 || $[122] !== t24 || $[123] !== t25) {
      t26 = [t22, t23, t24, t25];
      $[120] = t22;
      $[121] = t23;
      $[122] = t24;
      $[123] = t25;
      $[124] = t26;
    } else {
      t26 = $[124];
    }
    const authorizationFields = t26;
    let t27;
    if ($[125] !== authorizationTaskAction) {
      t27 = () => authorizationTaskAction;
      $[125] = authorizationTaskAction;
      $[126] = t27;
    } else {
      t27 = $[126];
    }
    const authorizationTaskFields = [{
      key: "action",
      label: "Action",
      getValue: t27
    }, {
      key: "dueDate",
      label: "Due Date",
      getValue: () => authorizationTaskDueDate
    }, {
      key: "priority",
      label: "Priority",
      getValue: _temp2
    }, {
      key: "role",
      label: "Assign to Role",
      getValue: _temp3
    }, {
      key: "case",
      label: "Case",
      getValue: () => syncedCaseName
    }];
    let t28;
    if ($[127] !== referral.diagnosisCode || $[128] !== referral.diagnosisText) {
      t28 = referral.diagnosisCode ? {
        diagnosisCode: referral.diagnosisCode,
        diagnosisText: referral.diagnosisText
      } : null;
      $[127] = referral.diagnosisCode;
      $[128] = referral.diagnosisText;
      $[129] = t28;
    } else {
      t28 = $[129];
    }
    const diagnosisFallback = t28;
    let t29;
    if ($[130] !== patient) {
      t29 = <PatientCard patient={patient} />;
      $[130] = patient;
      $[131] = t29;
    } else {
      t29 = $[131];
    }
    let t30;
    if ($[132] === Symbol.for("react.memo_cache_sentinel")) {
      t30 = <p className="font-medium">All of the following must be true for a case to match the incoming referral:</p>;
      $[132] = t30;
    } else {
      t30 = $[132];
    }
    let t31;
    if ($[133] === Symbol.for("react.memo_cache_sentinel")) {
      t31 = <div>{t30}<ol className="mt-1 list-decimal space-y-1 pl-5 text-muted-foreground"><li>The case is Active.</li><li>The patient has had an appointment for the case within the past 90 days or has one upcoming in the next 90 days, or the case was created within the past 30 days.</li><li><code>serviceLine</code> from intake matches the Discipline in Clinicient (i.e. both Physical Therapy).</li><li>Body part, diagnosis, and reason for referral are similar to the body part in the existing case name and the ICD-10 codes listed on the case.</li></ol><p className="mt-1 text-muted-foreground">If multiple cases match, choose the one with the more recent associated appointment.</p></div>;
      $[133] = t31;
    } else {
      t31 = $[133];
    }
    let t32;
    if ($[134] === Symbol.for("react.memo_cache_sentinel")) {
      t32 = <p className="font-medium">ICD-10 codes</p>;
      $[134] = t32;
    } else {
      t32 = $[134];
    }
    let t33;
    if ($[135] !== diagnoses || $[136] !== diagnosisFallback) {
      t33 = <div className="space-y-1">{t32}<DiagnosisListTable diagnoses={diagnoses} fallbackDiagnosis={diagnosisFallback} /></div>;
      $[135] = diagnoses;
      $[136] = diagnosisFallback;
      $[137] = t33;
    } else {
      t33 = $[137];
    }
    let t34;
    if ($[138] !== caseDetailFields || $[139] !== diagnoses || $[140] !== diagnosisFallback || $[141] !== existingCase || $[142] !== referral) {
      t34 = existingCase ? <p className="text-sm">Verify the case details in Clinicient before completing the task.</p> : <div className="space-y-3"><FieldLayout stacked={true} fields={caseDetailFields} patient={referral} /><div className="space-y-1"><p className="text-sm font-medium">Diagnosis codes</p><DiagnosisListTable diagnoses={diagnoses} fallbackDiagnosis={diagnosisFallback} /></div></div>;
      $[138] = caseDetailFields;
      $[139] = diagnoses;
      $[140] = diagnosisFallback;
      $[141] = existingCase;
      $[142] = referral;
      $[143] = t34;
    } else {
      t34 = $[143];
    }
    let t35;
    if ($[144] === Symbol.for("react.memo_cache_sentinel")) {
      t35 = <p>In the Attachments section of the patient chart, upload the document with:</p>;
      $[144] = t35;
    } else {
      t35 = $[144];
    }
    let t36;
    let t37;
    if ($[145] === Symbol.for("react.memo_cache_sentinel")) {
      t36 = <div><dt className="inline font-medium">Attachment Type: </dt><dd className="inline">Case Record</dd></div>;
      t37 = <dt className="inline font-medium">Case: </dt>;
      $[145] = t36;
      $[146] = t37;
    } else {
      t36 = $[145];
      t37 = $[146];
    }
    const t38 = <div>{t37}<dd className="inline">{syncedCaseName}</dd></div>;
    let t39;
    if ($[147] === Symbol.for("react.memo_cache_sentinel")) {
      t39 = <dt className="inline font-medium">Description: </dt>;
      $[147] = t39;
    } else {
      t39 = $[147];
    }
    let t40;
    if ($[148] !== documentDescription) {
      t40 = <div>{t39}<dd className="inline">{documentDescription}</dd></div>;
      $[148] = documentDescription;
      $[149] = t40;
    } else {
      t40 = $[149];
    }
    let t41;
    if ($[150] !== t38 || $[151] !== t40) {
      t41 = <dl className="space-y-1 [overflow-wrap:anywhere]">{t36}{t38}{t40}</dl>;
      $[150] = t38;
      $[151] = t40;
      $[152] = t41;
    } else {
      t41 = $[152];
    }
    let t42;
    if ($[153] === Symbol.for("react.memo_cache_sentinel")) {
      t42 = <div className="rounded-md border bg-muted/30 px-3 py-2"><CopyableContent text="Referral document uploaded to attachments." alwaysShow={true} /></div>;
      $[153] = t42;
    } else {
      t42 = $[153];
    }
    const definitions = {
      [StepKey.REQUIRE_PATIENT_PHONE]: {
        title: "Check required patient information",
        status: stepStatus(faxStep, faxId !== null || faxSkipped, onHold && faxStep?.id === lastStep?.id, faxSkipped),
        summary: faxId ? "The patient is missing a phone number. A fax has been sent to the sender requesting it. No action is needed here; the referral continues to be processed." : faxSkipped ? "The patient has all required information." : "The patient information check has not completed yet.",
        content: <DecisionMetadata items={[{
          label: "Fax recipient",
          value: outputId(faxStep, "targetFaxNumber") ?? "Not recorded"
        }, {
          label: "Fax record",
          value: faxId ?? "Not recorded"
        }]} />
      },
      [StepKey.ENSURE_PATIENT]: {
        title: "Confirm patient chart exists in Clinicient with the following demographic information",
        status: stepStatus(patientStep, patientId !== null),
        summary: "For existing patients, only mobile phone number must be updated to reflect the information shown here. For new patients, ensure all information below is included in the chart.",
        content: <div className="space-y-3">{t29}<DecisionMetadata items={[{
            label: "Clinicient patient ID",
            value: patientId ?? "Not recorded"
          }, {
            label: "Patient",
            value: recordOrigin(patientStep?.output?.created)
          }]} /></div>
      },
      [StepKey.CLINICIENT_ENSURE_CASE]: {
        title: "Find or create the referral case",
        status: stepStatus(caseStep, caseId !== null),
        summary: "Match the referral to an existing Clinicient case or create a new case when appropriate. Ambiguous matches require review.",
        content: <div className="space-y-4 text-sm">{t31}<FieldLayout stacked={true} fields={caseIntakeFields} patient={referral} />{t33}<DecisionMetadata items={[{
            label: "Clinicient case ID",
            value: caseId ?? "Not recorded"
          }, {
            label: "Case",
            value: recordOrigin(caseStep?.output?.created)
          }]} /></div>
      },
      [StepKey.CLINICIENT_CONFIGURE_CASE]: {
        title: "Add case details",
        status: stepStatus(configureStep, caseId !== null, configureHeld, existingCase),
        summary: existingCase ? "Preserve the matched case\u2019s existing configuration." : "Ensure that all details below are added in the Case Details, Referral, and Diagnosis Codes sections of the case. Also ensure that \"Authorized to Received Payment\" is selected and that the \"Work Related\" checkbox is selected if the referral is from worker's comp. Add company if needed.",
        content: t34
      },
      [StepKey.CLINICIENT_CASE_DOCUMENT_UPLOAD]: {
        title: "Upload the referral document as a case record",
        status: stepStatus(uploadStep, documentId !== null),
        summary: "Upload the referral document to the case as a Case Record attachment.",
        content: <div className="space-y-3 text-sm">{t35}{t41}<DecisionMetadata items={[{
            label: "Clinicient document ID",
            value: documentId ?? "Not recorded"
          }]} /></div>
      },
      [StepKey.CLINICIENT_DOCUMENT_NOTE]: {
        title: "Add referral upload note to patient chart",
        status: stepStatus(noteStep, noteId !== null),
        summary: "To the Notes section of the patient chart, add the following note:",
        content: <div className="space-y-3">{t42}<DecisionMetadata items={[{
            label: "Clinicient note ID",
            value: noteId ?? "Not recorded"
          }]} /></div>
      },
      [StepKey.CLINICIENT_SYNC_INSURANCE]: {
        title: "Add primary insurance to case",
        status: stepStatus(insuranceStep, insuranceSkipped || insuranceStep?.output?.outcome === "synced" && insurancePlanId !== null, false, insuranceSkipped),
        summary: insuranceSkipped ? "Preserve the matched case\u2019s existing insurance. Verify coverage in Clinicient." : "Ensure that all of the following information has been added to the case. The subscriber is always configured as Self. Add secondary insurance if needed \u2014 secondary insurance must be added manually.",
        content: insuranceSkipped ? undefined : <div className="space-y-3"><FieldLayout stacked={true} fields={primaryInsuranceFields} patient={referral} />{secondaryEhrPayerName || referral.secondaryCoverage ? <div className="space-y-2"><p className="text-sm font-medium">Secondary insurance (add manually)</p><FieldLayout stacked={true} fields={secondaryInsuranceFields} patient={referral} /></div> : null}<DecisionMetadata items={[{
            label: "Clinicient insurance plan ID",
            value: insurancePlanId ?? "Not recorded"
          }]} /></div>
      },
      [StepKey.CLINICIENT_ENSURE_AUTHORIZATION]: {
        title: "Add authorization number to case, if needed.",
        status: stepStatus(authorizationStep, authorizationSkipped || authorizationId !== null, false, authorizationSkipped),
        summary: authorizationSkipped ? "No authorization number was provided for this referral." : "If the incoming referral includes an authorization number, it must be added to the case (unless the same authorization is already listed in the case). If start and end date are not listed, use today's date for both. If number of visits is not listed, use 1 as a placeholder.",
        content: authorizationSkipped ? undefined : <div className="space-y-3"><FieldLayout stacked={true} fields={authorizationFields} patient={referral} /><DecisionMetadata items={[{
            label: "Clinicient authorization ID",
            value: authorizationId ?? "Not recorded"
          }, {
            label: "Authorization",
            value: recordOrigin(authorizationStep?.output?.created)
          }]} /></div>
      },
      [StepKey.CLINICIENT_AUTHORIZATION_DOCUMENT_UPLOAD]: {
        title: "Attach the referral document to the authorization",
        status: stepStatus(authorizationUploadStep, authorizationUploadSkipped || authorizationDocumentId !== null, false, authorizationUploadSkipped),
        summary: authorizationUploadSkipped ? "No authorization attachment is needed for this referral." : "Unless there is a matching authorization number with a document already attached (check by clicking the paperclip button next to the authorization information), the document must be uploaded as an attachment to the authorization by clicking this paperclip button.",
        content: authorizationUploadSkipped ? undefined : <DecisionMetadata items={[{
          label: "Clinicient authorization document ID",
          value: authorizationDocumentId ?? "Not recorded"
        }, {
          label: "Attachment",
          value: recordOrigin(authorizationUploadStep?.output?.uploaded)
        }]} />
      },
      [StepKey.CLINICIENT_AUTHORIZATION_TASK]: {
        title: "Create the authorization task",
        status: stepStatus(authorizationTaskStep, authorizationTaskSkipped || authorizationTaskId !== null, false, authorizationTaskSkipped),
        summary: authorizationTaskSkipped ? "For newly-created cases or referral documents that do not contain an authorization number, no authorization task is required." : "If the document is a Continuation of Care, create a \"Re-Authorization\" task. If the document is not, create an \"Authorization\" task.",
        content: authorizationTaskSkipped ? undefined : <div className="space-y-3"><FieldLayout stacked={true} fields={authorizationTaskFields} patient={referral} /><DecisionMetadata items={[{
            label: "Clinicient task ID",
            value: authorizationTaskId ?? "Not recorded"
          }]} /></div>
      },
      [StepKey.REFERRAL_SHEET_EXPORT]: {
        title: "Export to the referral spreadsheet",
        status: stepStatus(exportStep, exportRange !== null, false, exportSkippedForExistingAppointment),
        summary: exportSkippedForExistingAppointment ? "Skipped spreadsheet export because the case already has an upcoming appointment within 90 days." : exportStep?.status === TaskStepStatus.COMPLETED ? "The referral row was exported and confirmed in the spreadsheet." : exportStep?.status === TaskStepStatus.FAILED ? "Export failed. Fix the issue and retry before verifying this referral." : "The final automated step exports this referral to the tracking spreadsheet.",
        content: exportStep?.status === TaskStepStatus.FAILED ? <ReferralExportResult key={task.updatedAt} task={task} /> : exportRange && !exportSkippedForExistingAppointment ? <DecisionMetadata items={[{
          label: "Spreadsheet row",
          value: exportRange
        }]} /> : undefined
      }
    };
    let t43;
    if ($[154] !== task.files) {
      t43 = task.files.find(_temp4)?.note;
      $[154] = task.files;
      $[155] = t43;
    } else {
      t43 = $[155];
    }
    note = t43;
    const informationNeededStep = onHold ? lastStep : undefined;
    const stepKeys = getConfluentReferralStepKeys(orderedTaskSteps.map(_temp5));
    hasInformationNeededStep = stepKeys.some(key_3 => key_3 === informationNeededStep?.stepKey);
    steps = stepKeys.map(key_4 => {
      const step_1 = definitions[key_4];
      const isBlockingStep = key_4 === lastStep?.stepKey && (onHold || task.status === TaskStatus.FAILED);
      const needsAction = key_4 === informationNeededStep?.stepKey;
      return {
        ...step_1,
        status: step_1.status === "failed" ? "failed" : needsAction ? "needs_action" : step_1.status,
        content: needsAction ? <><WorkflowStepInformationNeeded note={note} />{step_1.content}</> : step_1.content,
        continuation: isBlockingStep && !pendingApproval && lastStep && key_4 !== StepKey.REFERRAL_SHEET_EXPORT ? {
          instruction: "After fixing the issue, continue from this step. Completed steps will be preserved.",
          control: <WorkflowStepResumeButton key={`${lastStep.id}:${task.updatedAt}`} task={task} step={lastStep} />
        } : undefined
      };
    });
    const allSynced = steps.every(_temp6);
    isCompleted = task.status === TaskStatus.COMPLETED;
    isProcessing = task.status === TaskStatus.PROCESSING;
    let t44;
    if ($[156] !== taskSteps) {
      t44 = taskSteps.some(_temp7);
      $[156] = taskSteps;
      $[157] = t44;
    } else {
      t44 = $[157];
    }
    const hasFailedStep = t44;
    let t45;
    if ($[158] !== taskSteps) {
      t45 = taskSteps.some(_temp8);
      $[158] = taskSteps;
      $[159] = t45;
    } else {
      t45 = $[159];
    }
    const hasPausedStep = t45;
    let t46;
    if ($[160] !== taskSteps) {
      t46 = taskSteps.some(_temp9);
      $[160] = taskSteps;
      $[161] = t46;
    } else {
      t46 = $[161];
    }
    hasAsyncStep = t46;
    const workflowNeedsAttention = onHold || hasFailedStep || hasPausedStep;
    const canVerify = task.status === TaskStatus.VERIFICATION_NEEDED && allSynced && !pendingApproval && !workflowNeedsAttention && !hasAsyncStep;
    needsAttention = workflowNeedsAttention || Boolean(pendingApproval) || steps.some(_temp10);
    let t47;
    if ($[162] === Symbol.for("react.memo_cache_sentinel")) {
      t47 = <p className="text-sm">Verify the insurance and referral documents in Clinicient. Complete any remaining secondary insurance and authorization work, then use Correct or Incorrect at the bottom to complete this task.</p>;
      $[162] = t47;
    } else {
      t47 = $[162];
    }
    steps.push({
      title: "Verify in Clinicient",
      status: isCompleted ? "verified" : hasFailedStep ? "failed" : workflowNeedsAttention || pendingApproval || canVerify ? "needs_action" : "waiting",
      summary: isCompleted ? "This task has been marked complete." : pendingApproval ? "Complete the pending workflow confirmation before verifying this task." : workflowNeedsAttention ? "Resolve the outstanding workflow issue before verifying this task." : isProcessing || hasAsyncStep ? "The workflow is continuing. Verify the results when it finishes." : !allSynced ? "Resolve the incomplete sync steps above before verifying this task." : "Confirm the patient, case, referral document, chart note, and insurance in Clinicient and complete the remaining manual work.",
      content: t47
    });
    $[0] = pendingApproval;
    $[1] = referral;
    $[2] = task;
    $[3] = taskSteps;
    $[4] = hasAsyncStep;
    $[5] = hasInformationNeededStep;
    $[6] = isCompleted;
    $[7] = isProcessing;
    $[8] = needsAttention;
    $[9] = note;
    $[10] = steps;
  } else {
    hasAsyncStep = $[4];
    hasInformationNeededStep = $[5];
    isCompleted = $[6];
    isProcessing = $[7];
    needsAttention = $[8];
    note = $[9];
    steps = $[10];
  }
  const t1 = isProcessing || hasAsyncStep;
  const t2 = !isCompleted && taskSteps.length === 0 && !needsAttention ? "No Clinicient sync steps have been recorded for this task yet." : undefined;
  let t3;
  if ($[163] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <p className="text-sm text-muted-foreground">SMS eligibility is assessed on the Scheduling Eligibility Verification task after this referral is completed.</p>;
    $[163] = t3;
  } else {
    t3 = $[163];
  }
  let t4;
  if ($[164] !== hasInformationNeededStep || $[165] !== needsAttention || $[166] !== note) {
    t4 = needsAttention && note && !hasInformationNeededStep ? <p className="whitespace-pre-wrap text-sm">{note}</p> : null;
    $[164] = hasInformationNeededStep;
    $[165] = needsAttention;
    $[166] = note;
    $[167] = t4;
  } else {
    t4 = $[167];
  }
  let t5;
  if ($[168] !== t4) {
    t5 = <>{t3}{t4}</>;
    $[168] = t4;
    $[169] = t5;
  } else {
    t5 = $[169];
  }
  let t6;
  if ($[170] !== steps || $[171] !== t1 || $[172] !== t2 || $[173] !== t5) {
    t6 = <ReferralWorkflowTimeline steps={steps} isProcessing={t1} waitingMessage={t2} summaryContent={t5} />;
    $[170] = steps;
    $[171] = t1;
    $[172] = t2;
    $[173] = t5;
    $[174] = t6;
  } else {
    t6 = $[174];
  }
  return t6;
}
function _temp10(step_6) {
  return step_6.status === "needs_action" || step_6.status === "failed";
}
function _temp9(step_5) {
  return step_5.status === TaskStepStatus.ASYNC;
}
function _temp8(step_4) {
  return step_4.status === TaskStepStatus.PAUSED;
}
function _temp7(step_3) {
  return step_3.status === TaskStepStatus.FAILED;
}
function _temp6(step_2) {
  return step_2.status === "automated" || step_2.status === "not_needed";
}
function _temp5(step_0) {
  return step_0.stepKey;
}
function _temp4(file) {
  return file.type === FileType.INBOUND;
}
function _temp3() {
  return "Front Desk";
}
function _temp2() {
  return "Medium";
}
function _temp(a, b) {
  return a.stepIndex - b.stepIndex;
}